import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User as UserIcon, Menu, X, LayoutDashboard, Megaphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useContent } from "@/context/ContentProvider";
import Logo from "@/components/Logo";

const DEFAULT_NAV = [
  { label: "Inicio", link: "/" },
  { label: "Catálogo", link: "/catalogo" },
  { label: "Terapia", link: "/catalogo?category=terapia_individual" },
  { label: "Cursos", link: "/catalogo?category=cursos" },
  { label: "Nosotros", link: "/#nosotros" },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { t, pages } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    ...(t("nav.items", DEFAULT_NAV) || []),
    ...pages.map((p) => ({ label: p.title, link: `/p/${p.slug}` })),
  ];
  const isActive = (link) => {
    const base = (link || "/").split("?")[0].split("#")[0];
    if (base === "/") return location.pathname === "/";
    return location.pathname.startsWith(base);
  };
  const logoSrc = t("site.logo_url", "/logo-emblem.png");
  const siteName = t("site.name", "AWEN");
  const bannerEnabled = !!t("banner.enabled", false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#4b3a2f]/95 shadow-[0_10px_40px_rgba(44,32,24,0.35)] backdrop-blur-md" : "bg-[#4b3a2f]"
      }`}
    >
      {bannerEnabled && (
        <Link
          to={t("banner.link", "/catalogo")}
          data-testid="top-banner"
          className="flex items-center justify-center gap-2 bg-[#c9a24b] px-4 py-2 text-center text-xs font-semibold text-[#4b3a2f] transition-colors hover:bg-[#b8913b]"
        >
          <Megaphone size={13} />
          {t("banner.text", "")}
          {t("banner.link_text", "") && <span className="underline">{t("banner.link_text", "")}</span>}
        </Link>
      )}
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home-link">
          <img src={logoSrc} alt={siteName} className="h-11 w-11 rounded-full object-cover ring-1 ring-[#c9a24b]/50" />
          <span className="hidden flex-col leading-none sm:flex">
            <span className="font-serif-d text-2xl font-semibold tracking-[0.22em] text-[#f2e9dd]">{siteName}</span>
            <span className="font-mono-d text-[9px] uppercase tracking-[0.24em] text-[#cdb79e]">
              {t("site.tagline", "Bienestar emocional")}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {navItems.map((n) => (
            <Link
              key={n.label}
              to={n.link}
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-[#c9a24b] ${
                isActive(n.link) ? "text-[#c9a24b]" : "text-[#f2e9dd]/85"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate("/carrito")}
            data-testid="nav-cart-button"
            aria-label="Carrito"
            className="relative flex h-11 w-11 items-center justify-center rounded-full border border-[#c9a24b]/40 text-[#f2e9dd] transition-colors duration-300 hover:bg-[#c9a24b] hover:text-[#4b3a2f]"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span
                data-testid="nav-cart-badge"
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#c9a24b] px-1 text-[11px] font-bold text-[#4b3a2f]"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="hidden items-center gap-2 sm:flex">
              {user.role === "admin" && (
                <button
                  onClick={() => navigate("/admin")}
                  data-testid="nav-admin-button"
                  className="flex h-11 items-center gap-2 rounded-full border border-[#c9a24b]/40 px-4 text-sm text-[#f2e9dd] transition-colors hover:bg-[#c9a24b] hover:text-[#4b3a2f]"
                >
                  <LayoutDashboard size={16} /> Panel
                </button>
              )}
              <button
                onClick={handleLogout}
                data-testid="nav-logout-button"
                className="flex h-11 items-center gap-2 rounded-full bg-[#c9a24b] px-5 text-sm font-semibold text-[#4b3a2f] transition-all hover:bg-[#b8913b]"
              >
                <UserIcon size={16} /> {user.name?.split(" ")[0]}
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate(t("nav.cta_link", "/registrarse"))}
              data-testid="nav-register-button"
              className="hidden h-11 items-center rounded-full bg-[#c9a24b] px-5 text-sm font-semibold text-[#4b3a2f] transition-all hover:bg-[#b8913b] sm:flex"
            >
              {t("nav.cta_text", "Crear cuenta")}
            </button>
          )}

          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-button"
            aria-label="Menú"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#c9a24b]/40 text-[#f2e9dd] lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#c9a24b]/20 bg-[#4b3a2f] lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {navItems.map((n) => (
                <Link key={n.label} to={n.link} className="rounded-lg px-3 py-3 text-[#f2e9dd] hover:bg-white/5">
                  {n.label}
                </Link>
              ))}
              {user ? (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" className="rounded-lg px-3 py-3 text-[#c9a24b]">
                      Panel de administrador
                    </Link>
                  )}
                  <button onClick={handleLogout} className="rounded-lg px-3 py-3 text-left text-[#f2e9dd]">
                    Cerrar sesión ({user.name})
                  </button>
                </>
              ) : (
                <Link to={t("nav.cta_link", "/registrarse")} className="rounded-lg px-3 py-3 text-[#c9a24b]">
                  {t("nav.cta_text", "Crear cuenta")} / Iniciar sesión
                </Link>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
